import { Box, Card, CardHeader, Flex, Heading, Text } from '@chakra-ui/react'
import { CardBody } from '@chakra-ui/react'
import { Icons } from '@ds/Icons'
import { DDSTheme } from '@ds/index'
import { ddsTokens } from '@ds/index'
import { parseISO } from 'date-fns'
import { useMemo, useState } from 'react'
import {
	Area,
	Bar,
	CartesianGrid,
	ComposedChart,
	Legend,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts'

import { useGetTeamMembersQuery } from '@/app/user/api/members'
import { useTeamId } from '@/app/user/api/teams/selectors'

import { useGetMeetingsForMemberQuery } from '../api'
import { Granularity, MeetingData, TimeFrame } from '../types'
import { formatPeriodLabelForChart } from '../utils'
import ChartSpinner from './ChartSpinner'
import ChartTooltip from './ChartTooltip'
import { DisplayTag } from './DisplayTag'
import LegendContent from './LegendContent'
import { SelectMenu } from './SelectMenu'

type Props = {
	meetings: MeetingData[] | undefined
	granularity: Granularity
	datePreset: TimeFrame
	isLoadingAllMeetings?: boolean
}

const RoundedTopBar = (props: any) => {
	const { x, y, width, height, fill } = props
	const radius = 4

	if (height < 2) {
		const lineHeight = 2
		return (
			<rect
				x={x}
				y={y - lineHeight / 2}
				width={width}
				height={lineHeight}
				fill={fill}
			/>
		)
	}

	return (
		<path
			d={`
				M${x},${y + height}
				L${x},${y + radius}
				Q${x},${y} ${x + radius},${y}
				L${x + width - radius},${y}
				Q${x + width},${y} ${x + width},${y + radius}
				L${x + width},${y + height}
				L${x},${y + height}
				Z
			`}
			fill={fill}
		/>
	)
}

const AverageTimeToMeet = ({
	meetings: teamAggregatedMeetings,
	granularity,
	datePreset,
	isLoadingAllMeetings = false,
}: Props) => {
	const teamId = useTeamId()
	const { data: teamMembers = [] } = useGetTeamMembersQuery(teamId)
	const [selectedMembers, setSelectedMembers] = useState<
		{ label: string; value: string }[]
	>([])

	const selectedMemberIds = useMemo(
		() => selectedMembers.map((m) => m.value),
		[selectedMembers],
	)

	const { data: memberTimeGroupedData, isLoading: isLoadingMemberData } =
		useGetMeetingsForMemberQuery(
			{
				memberIds: selectedMemberIds,
				datePreset,
				granularity,
			},
			{ skip: selectedMemberIds.length === 0 },
		)

	const isLoading = isLoadingMemberData || isLoadingAllMeetings

	const processedData = useMemo(() => {
		if (!teamAggregatedMeetings || teamAggregatedMeetings.length === 0) {
			return []
		}

		const sortedTeamMeetings = [...teamAggregatedMeetings].sort(
			(a, b) =>
				parseISO(a.time_group).getTime() - parseISO(b.time_group).getTime(),
		)

		return sortedTeamMeetings.map((teamMeetingForPeriod) => {
			const periodLabel = formatPeriodLabelForChart(
				teamMeetingForPeriod.time_group,
				granularity,
			)

			const teamAverageSeconds =
				teamMeetingForPeriod.average_time_to_meet_seconds
			const teamAverageDays =
				teamAverageSeconds !== null && teamAverageSeconds !== undefined
					? teamAverageSeconds / (60 * 60 * 24)
					: null

			const dataPoint: Record<string, any> = {
				period: periodLabel,
				average: teamAverageDays,
			}

			if (selectedMembers.length > 0 && memberTimeGroupedData) {
				selectedMembers.forEach((member) => {
					const memberDataForPeriod = memberTimeGroupedData.find(
						(mdata) =>
							mdata.member_id === member.value &&
							mdata.time_group === teamMeetingForPeriod.time_group,
					)

					if (
						memberDataForPeriod &&
						memberDataForPeriod.average_time_to_meet_seconds !== null &&
						memberDataForPeriod.average_time_to_meet_seconds !== undefined
					) {
						dataPoint[member.label] =
							memberDataForPeriod.average_time_to_meet_seconds / (60 * 60 * 24)
					} else {
						dataPoint[member.label] = 0
					}
				})
			}
			return dataPoint
		})
	}, [
		teamAggregatedMeetings,
		granularity,
		selectedMembers,
		memberTimeGroupedData,
	])

	const tokens = ddsTokens[DDSTheme.light]

	const averageLineColor = tokens.primitive.light.gray[700]

	const memberColors = [
		tokens.primitive.light.warning[400],
		tokens.primitive.light.info[400],
		tokens.primitive.light.danger[400],
		tokens.primitive.light.success[400],
		tokens.primitive.light.primary[400],
	]

	const formatTooltipValue = (value: any) => {
		if (value === null || value === undefined) return 'No data'
		if (value === 0) return '0 d'
		return `${Number(value).toFixed(1)} d`
	}

	return (
		<Card
			shadow="sm"
			p={4}
			border={`1px solid ${tokens.primitive.light.gray[100]}`}
		>
			<CardHeader p={0} mb={2}>
				<Box>
					<Flex justifyContent="space-between">
						<Box>
							<Heading size="sm">Inbound Speed-to-Lead</Heading>
							<Text fontSize="sm" color="gray.500">
								Average number of days between a qualified form-fill and meeting
								booked date
							</Text>
						</Box>
						<Box pr={5}>
							<SelectMenu
								options={
									teamMembers.map((member) => ({
										label: `${member.first_name} ${member.last_name}`,
										value: member.id,
										avatarUrl: member.avatar_url,
									})) || []
								}
								selectedValues={selectedMembers.map((m) => m.value)}
								onChange={(newValue) => {
									setSelectedMembers([...newValue])
								}}
								placeholder="Compare up to 5 team members"
								isLoading={isLoadingMemberData}
								maxSelections={5}
							/>
						</Box>
					</Flex>
					{selectedMembers.length > 0 && (
						<Box mt={2}>
							<Flex wrap="wrap" gap={2}>
								{selectedMembers.map((member, index) => (
									<DisplayTag
										key={member.value}
										value={{
											label: member.label,
											color: memberColors[index % memberColors.length],
											icon: Icons.user,
										}}
										onRemove={() => {
											setSelectedMembers(
												selectedMembers.filter((m) => m.value !== member.value),
											)
										}}
									/>
								))}
							</Flex>
						</Box>
					)}
				</Box>
			</CardHeader>
			<CardBody p={0} position="relative" flexGrow={1}>
				{isLoading && <ChartSpinner />}
				<ResponsiveContainer width="100%" height={250}>
					<ComposedChart
						data={processedData}
						margin={{ top: 24, right: 20, bottom: 0 }}
					>
						<CartesianGrid
							horizontal
							vertical
							stroke="#E2E2E4"
							strokeDasharray="4 2"
						/>
						<XAxis
							dataKey="period"
							tickLine={false}
							tick={{ fill: '#666666' }}
							dy={5}
						/>
						<YAxis
							tickLine={false}
							stroke="transparent"
							tick={{ fill: '#666666' }}
							tickFormatter={(value) =>
								value === null ? 'N/A' : `${Number(value)}`
							}
						/>
						<Tooltip
							cursor={{
								fill: '#ECECED',
								opacity: 0.6,
							}}
							content={(props) => (
								<ChartTooltip {...props} formatValue={formatTooltipValue} />
							)}
						/>
						<Legend content={<LegendContent />} />

						<Area
							type="monotone"
							dataKey="average"
							stroke="transparent"
							fill={tokens.primitive.light.gray[100]}
							legendType="none"
							tooltipType="none"
						/>

						{selectedMembers.map((member, index) => (
							<Bar
								key={`bar-${member.value}`}
								dataKey={member.label}
								fill={memberColors[index % memberColors.length]}
								barSize={80}
								name={member.label}
								shape={<RoundedTopBar />}
							/>
						))}

						<Line
							type="monotone"
							dataKey="average"
							stroke={averageLineColor}
							strokeWidth={2}
							strokeDasharray="4 2"
							dot={false}
							activeDot={false}
							name="Team average"
							connectNulls
							isAnimationActive={false}
						/>

						<Line
							type="monotone"
							dataKey="average"
							stroke="transparent"
							dot={{
								stroke: averageLineColor,
								strokeWidth: 2,
								r: 4,
								fill: 'white',
							}}
							activeDot={{
								strokeWidth: 2,
								r: 4,
								fill: averageLineColor,
							}}
							legendType="none"
							tooltipType="none"
							connectNulls
						/>
					</ComposedChart>
				</ResponsiveContainer>
			</CardBody>
		</Card>
	)
}

export default AverageTimeToMeet
